(ns app.front.util
  (:require-macros [app.front.util])
  (:require ["proj4" :default proj4]
            [clojure.set :as set]
            [clojure.string :as str]
            [core.db.entity-api :as entity-api]
            [core.front.href :as href]
            [core.util.util :as util]
            [goog.events :as googe]
            [goog.object :as go]
            [re-frame.core-instrumented :as rf]
            [reagent.core :as r]))

(defn- project [coordinates]
  (proj4 "EPSG:3857" "WGS84" (clj->js coordinates)))

(defn- project-lat-long
  [coordinates]
  (cond
    (and (vector? coordinates)
         (= 2 (count coordinates))
         (every? number? coordinates))
    (let [[x y] (project coordinates)]
      {:latitude y :longitude x})

    (entity-api/entity? coordinates)
    (project-lat-long {:x (:location.center/x coordinates)
                       :y (:location.center/y coordinates)})

    (map? coordinates)
    (as-> (set/rename-keys coordinates {:location.center/x :x :location.center/y :y}) coordinates
          (when (every? number? ((juxt :x :y) coordinates)) (project coordinates))
          (when coordinates {:latitude (.-y coordinates) :longitude (.-x coordinates)}))))

(defn to-fixed [n digits]
  (when (number? n)
    (.toFixed n digits)))


(defn format-coordinate-pair [[x y]]
  {:x (to-fixed x 6)
   :y (to-fixed y 6)})


(defn lat-long
  "Input is either [x y], {:x x, :y y} or {:location.center/x x, :location.center/y y},
  returns map with :latitude and :longitude keys and values fixed to five digits."
  [coordinates]
  (when-let [coordinates (project-lat-long coordinates)]
    (-> (update coordinates :latitude to-fixed 5)
        (update :longitude to-fixed 5))))

(defn location-coordinates [workspace]
  (some->> workspace
           :entity/location
           lat-long
           ((juxt :latitude :longitude))
           (str/join ", ")))


(defn parse-int
  "DEPRECATED Get a number from string. Please prefer `clojure.core/parse-long` for new code."
  [s]
  (let [n (js/parseInt s 10)]
    (when-not (js/isNaN n)
      n)))


(defn parse-float
  [s]
  (let [n (js/parseFloat s)]
    (when-not (js/isNaN n)
      n)))


(defn parse-number [s step]
  (if (and (number? step) (< step 1))
    (let [n (js/parseFloat s 10)]
      (when-not (js/isNaN n)
        n))
    (parse-int s)))


(defn cs
  "Mostly deprecated. Just use a vector of class names. Nils are fine too.

  Except for cc/ :class-name parameter, that one really does need to be a string"
  [& names]
  (let [css (filter identity names)]
    (when-not (empty? css)
      (str/join " " css))))

(defn- css
  "Convenience function for :class definitions. Supports keywords,
  strings, vectors and kw-paths."
  [& classes]
  (->> classes flatten (remove nil?)
       (map util/split-kw-path)
       (apply concat)
       (map name)))

(defn css-flags
  "List of keys with truthy values.
  (css-flags :foo true :bar false) => '(\"foo\")"
  [& flags]
  (->> (apply hash-map flags)
       (filter val)
       keys
       css))

;; Callthrough for goog.events.getUniqueId.
;; Must be in the global scope.
(def unique-id googe/getUniqueId)

(defn money-string-whole-units [amount currency]
  (when (nil? currency)
    (.error js/console "money-string-whole-units without currency"))
  (when (and (number? amount) currency)
    (str
      (-> amount
          (.toLocaleString (case currency
                             "CAD" "en-CA"
                             "AUD" "en-AU"
                             "USD" "en-US"
                             "EUR" "fi-FI")
                           (clj->js {:style    "currency"
                                     :currency currency}))))))

(defn money-string
  "Formats the given money amount (in cents) according to currency
  locale. Supported currencies are CAD, AUD, USD and EUR."
  [amount currency]
  (money-string-whole-units (/ amount 100) currency))

(defn ellipsis
  "Cuts text after `number-of-chars` and appends ellipsis … character if the text is longer than
   `number-of-chars` + 2.
   e.g.:
   (ellipsis \"foobar\" 4) => \"foobar\"
   (ellipsis \"foobar\" 3) => \"foo…\""
  [text number-of-chars]
  (if (and (string? text) (> (count text) (+ number-of-chars 2)))
    (str (->> (take number-of-chars text) (apply str)) "…")
    text))

(defn oget
  "Safe way of accessing JavaScript object properties with `goog.object/get`. `obj` is the target
  object, `path` items can be either string, keyword or vector of those. The final access vector is
  flattened. Returns nil for bad paths."
  [obj & path]
  (loop [obj               obj
         [x & xs :as path] (flatten path)]
    (if (empty? path)
      obj
      (when-let [k (not-empty (cond
                                (keyword? x) (name x)
                                (nil? x) nil
                                :else (str x)))]
        (recur (go/get obj k)
               xs)))))

(defn scroll-to-top
  "Convenience wrapper for `window.scrollTo`."
  []
  (.scrollTo js/window #js {:behavior "smooth"
                            :top      0
                            :left     0})
  nil)

(defn scroll-to-bottom
  "Convenience wrapper for `window.scrollTo`."
  ([]
   (scroll-to-bottom true))
  ([smooth?]
   (.scrollTo js/window #js {:left     0
                             :top      (-> js/document .-body .-scrollHeight)
                             :behavior (if smooth? "smooth" "auto")})
   nil))

(defn tval
  "Extract the target value of an event"
  [event]
  (-> event .-target .-value))

(defn value-changed?
  "Check if input value has changed"
  [event]
  (not= (tval event) (-> event .-target .-defaultValue)))

(defn tr-date-frequency [tr date-frequency]
  (tr [(keyword "date.frequencies" date-frequency)]))

(defn tr-weekday [tr weekday]
  (tr [(keyword "date.weekdays" weekday)]))

(defn tr-weekday-short [tr weekday]
  (tr [(keyword "date.weekday-short" weekday)]))

(defn tr-month [tr month]
  (tr [(keyword "date.month" month)]))

(defn set-title [title]
  (set! (.-title js/document) (if-not (str/blank? title)
                                (str "Cloudpermit - " title)
                                "Cloudpermit")))


(def not-blank? (complement str/blank?))

(defn str-and-desc [primary desc]
  (let [has-both? (every? not-blank?
                          [desc primary])]
    (str (when (not-blank? primary) primary)
         (when has-both? " (")
         (when (not-blank? desc) desc)
         (when has-both? ")"))))


(defn local-storage-set!
  "Sets a value in localStorage. If val is nil, removes the key instead."
  [key val]
  (if (nil? val)
    (.removeItem (.-localStorage js/window) key)
    (.setItem (.-localStorage js/window) key val)))


(defn local-storage-get [key]
  (.getItem (.-localStorage js/window) key))


(defn local-storage-set-json! [key val]
  (local-storage-set! key
                      (-> val
                          clj->js
                          js/JSON.stringify)))


(defn local-storage-get-json [key]
  (-> key
      local-storage-get
      js/JSON.parse
      js->clj))


(rf/reg-fx :local-storage-set!
  (fn [[key value]]
    (local-storage-set! key value)))

(rf/reg-fx :local-storage-set-json!
  (fn [key-value-pairs]
    (->> key-value-pairs
         (map (fn [[key value]] (local-storage-set-json! key value)))
         doall)))


(rf/reg-fx :local-storage-remove!
  ;; Note, this takes a list of keys, not key-value pairs
  (fn [[& keys]]
    (->> keys
         (run! #(local-storage-set! % nil)))))

(def key-code-enter 13)
(def key-code-esc 27)

(defn enter-press? [e]
  (= (.-keyCode e) key-code-enter))

(defn esc-press? [e]
  (= (.-keyCode e) key-code-esc))

(defn target-element-of-type? [e element-type]
  (= (-> e .-target .-type) element-type))

(defn scroll-to-element [elem]
  (.scrollIntoView elem #js {:behavior "smooth"})
  nil)

(defn scroll-to-id
  ([id]
   (scroll-to-id id 0))
  ([id tries]
   (when-not (empty? id)
     (if-let [elem (.getElementById js/document id)]
       (scroll-to-element elem)
       (when (< tries 30)
         (js/setTimeout #(scroll-to-id id (inc tries)) 300))))))

(defn scroll-to-anchor []
  (when-let [hash (.-hash js/window.location)]
    (scroll-to-id (subs hash 1))))

(defn get-anchor []
  (subs (.-hash js/window.location) 1))

(defn get-query-param [name]
  (-> (js/URL. js/window.location)
      .-searchParams
      (.get name)))

(rf/reg-fx
  :set-location
  (fn [[view params hash]]
    (js/window.location.replace
      (str (href/href view params) hash))))

(rf/reg-fx
  :navigate-to
  (fn [url]
    (js/window.location.assign url)))

(defn open-in-new-tab-or-window
  "Open the `url` in a new tab (or a window if user has their browser so configured) and focus on it.
  Unfortunately this cannot be reliably called via re-frame fx, because browsers (at least Firefox) pop-up
  blockers can prevent the opening unless it is a direct result from on-click event."
  [url]
  (when-let [new-window (.open js/window url "_blank")]
    (when (fn? (.-focus new-window))
      (try
        (.focus new-window)
        (catch :default _)))))

(defn single-memoize [f]
  (let [*invocation (atom nil)]
    (fn [& new-args]
      (:result (swap! *invocation
                      (fn [{:keys [args] :as invocation}]
                        (if (and invocation (= new-args args))
                          invocation
                          (let [result (apply f new-args)]
                            {:result result
                             :args   new-args}))))))))

(defn sanitize-id [id]
  (if (keyword? id)
    (->> [(some-> (namespace id) (str/replace "." "-"))
          (name id)]
         (remove nil?)
         (str/join "-"))
    (str id)))


(defn js-loader
  "Wrapper for a component that needs given script loaded before rendering.
   Onerror also returns loaded content, so handle errors where you call this."
  [{:keys [id src integrity module?]} _loading _loaded]
  (let [loaded? (r/atom false)]
    (r/create-class
      {:component-did-mount (fn [_]
                              (let [script-exists? (js/document.getElementById id)]
                                (if script-exists?
                                  (reset! loaded? true)
                                  (let [script (.createElement js/document "script")]
                                    (when module?
                                      (set! (.-type script) "module"))
                                    (set! (.-id script) id)
                                    (set! (.-src script) src)
                                    (when integrity
                                      (set! (.-integrity script) integrity)
                                      (set! (.-crossOrigin script) "anonymous"))
                                    (set! (.-onerror script) #(reset! loaded? true))
                                    (set! (.-onload script) #(reset! loaded? true))
                                    (-> js/document .-body (.append script))))))
       :reagent-render      (fn [_script loading loaded]
                              (if @loaded? loaded loading))})))

(defn make-callback-fn
  "Helper for creating stable reagent callbacks, in other words, returns a function
  that returns the same function if the given parameters are unchanged.

  Usage:
  ```
  (defn base-handler [optional initial parameters changing-param evt params] ...)
  (defn stable-component [{:keys [changing-param]}]
    (r/with-let [on-sth-fn (futil/make-callback-fn base-handler optional initial parameters)]
      [other-component {:on-sth (on-sth-fn changing-param)}]))
  ```

  You can use cljs.ui/defni to verify.

  See https://day8.github.io/re-frame/on-stable-dom-handlers/#more-advanced-again
  for inspiration"
  [f & args-0]
  (let [*state (atom nil)]
    (fn [& args-1-new]
      (-> *state
          (swap! (fn [{:keys [args-1 f-out] :as state}]
                   (if (and f-out (= args-1 args-1-new))
                     state
                     {:args-1 args-1-new
                      :f-out  (fn [& args2]
                                (apply f (concat args-0 args-1-new args2)))})))
          :f-out))))

(defn get-meta-content-by-name [name]
  (let [meta-tag (.querySelector js/document (str "meta[name='" name "']"))]
    (when meta-tag
      (.-content meta-tag))))

(defn- scroll-to-element-after-render [element-id]
  (r/after-render
    (fn []
      (when-let [el (js/document.getElementById element-id)]
        (.scrollIntoView el #js {:behavior "smooth" :block "start"})
        nil))))

(rf/reg-fx ::scroll-to-section
  (fn [section-id]
    (let [element-id (if (string? section-id)
                       section-id
                       (str "section--" (sanitize-id section-id)))]
      (scroll-to-element-after-render element-id))))
